/**
 * The call's SERVER-side story, on `call:<callId>`.
 *
 * The SFU can tell you a track appeared; only the API can tell you the other
 * side declined, hung up from another device, or never picked up. Both halves
 * are needed — a room with no remote publisher looks identical whether the
 * callee is still ringing or already gone.
 */
import { useEffect, useState } from 'react';
import { getRealtimeClient } from '@/lib/realtime';
import { useTopicSubscription } from '@/features/realtime/useTopicSubscription';
import { presenceActions } from '@/features/presence/presenceStore';
import { activeCallActions } from '../store/activeCallStore';
import { releaseCallMedia } from '../media/callMediaRegistry';
import { announcedCallStatus, callIsLive } from '../lib/callState';
import { useCallReconciliation } from './useCallReconciliation';

export interface CallLifecycle {
  readonly connected: boolean;
  readonly endedReason: string | null;
  /**
   * This call ended because it was MERGED into another one, and the people on
   * it have been moved there.
   *
   * Deliberately not an `endedReason`: there is no end screen to show, because
   * the user is already on their way into the surviving call. It exists for the
   * one surface that cannot follow them by itself — a desktop call window,
   * which is keyed to THIS call in the shell and whose survivor is opened by
   * the workspace, in a window this document cannot reach. Without it that
   * window is never told anything at all and never goes away.
   */
  readonly merged: boolean;
  /**
   * This call ended because it was MOVED TO A MEETING, and the people on it are
   * being taken there.
   *
   * The same shape of fact as `merged`, and it exists for the same two reasons.
   * There is no end screen to show — `useEscalatedCall` is redeeming the join
   * code and navigating right now, and "Call ended" flashing first is precisely
   * the half-second of dead room that flag was invented to prevent. And a
   * DESKTOP CALL WINDOW cannot follow by itself: the workspace goes to the
   * meeting (a meeting must never open in a window the shell keys to a call),
   * so without this the call window would sit in the taskbar holding a call
   * that no longer exists, still reporting live media.
   *
   * Before this existed, `call.escalated` was in no table on this client at
   * all: it fell through the event handler untouched and the 30-second
   * reconciliation poll eventually produced "Call ended".
   */
  readonly escalated: boolean;
}

const END_REASON: Readonly<Record<string, string>> = {
  'call.declined': 'Call declined',
  'call.missed': 'No answer',
  'call.ended': 'Call ended',
};

/**
 * THE CALL IS OVER FOR ME: STOP BEING IN IT.
 *
 * Rendering the end screen is not leaving. Until now that was the whole of the
 * client's response to `call.ended` — set a string, let `CallSurface` swap
 * `<CallRoom>` for `<CallEndedScreen>`, and rely on React unmounting the room
 * to reach `useCallSession`'s cleanup and, five files later, `track.stop()`.
 * It works, and test/callEnd.test.mjs holds it to it. But it is an EMERGENT
 * property of a render, and the one thing a call must never do is leave a
 * microphone publishing into a room the other party has left. So the exit is
 * taken directly, by name, from every path that learns the call is over:
 *
 *   • the realtime event, below;
 *   • the server reconciliation, when the event never arrived;
 *   • the user's own Hang up (`useCallControls`).
 *
 * All three are idempotent and all three may fire. Keyed by call id, because a
 * transfer or a merge ends one call while this document is already holding the
 * media for the one it moved into.
 *
 * PRESENCE GOES WITH IT. "In a call" must not outlive the call: this page keeps
 * rendering the ended screen, so no unmount is coming to clear it.
 */
function leaveCallLocally(callId: string): void {
  presenceActions.setFlags({ inCall: false });
  void releaseCallMedia(callId);
}

export function useCallLifecycle(callId: string): CallLifecycle {
  const [connected, setConnected] = useState(false);
  const [endedReason, setEndedReason] = useState<string | null>(null);
  const [merged, setMerged] = useState(false);
  const [escalated, setEscalated] = useState(false);

  useTopicSubscription([callId === '' ? null : `call:${callId}`]);

  /**
   * PRESENCE FOLLOWS THE CALL — "In a call" for exactly as long as the room is
   * open, and not one second longer.
   *
   * Here rather than on the toolbar because this hook is what <CallPage> mounts
   * for EVERY way into a call (placed, answered, reloaded, opened in its own
   * window) and tears down on every way out of one. A stuck flag resolves to Do
   * not disturb for the rest of the session, so it is dropped from three
   * independent places and each of them is safe to run twice:
   *   • here on unmount — navigation, route change, the room being replaced;
   *   • in the end-of-call branch below — the far end hung up while the page
   *     stays mounted on the ended screen, where no unmount is coming;
   *   • `useActivityMonitor`'s pagehide handler — the window being closed.
   */
  useEffect(() => {
    if (callId === '') return undefined;
    presenceActions.setFlags({ inCall: true });
    return () => presenceActions.setFlags({ inCall: false });
  }, [callId]);

  useEffect(() => {
    if (callId === '') return undefined;
    setConnected(false);
    setEndedReason(null);
    setMerged(false);
    setEscalated(false);
    const realtime = getRealtimeClient();
    realtime.connect();
    return realtime.onEvent(({ event }) => {
      if (!('callId' in event) || event.callId !== callId) return;
      if (event.type === 'call.connected' || event.type === 'call.resumed') {
        activeCallActions.markConnected(callId);
        setConnected(true);
        return;
      }
      /*
       * A CALL THAT ENDED BECAUSE IT WAS MERGED IS NOT OVER FOR ME. Its
       * participants have just been moved into the surviving call and
       * `useTransferredCall` is navigating them there; showing "Call ended"
       * first would flash a dead room in the half-second before the route
       * changes. The merge event itself is what moves them.
       *
       * BUT IT IS OVER FOR THIS SURFACE, and that distinction only appeared
       * when the call got a window of its own. `useTransferredCall` is mounted
       * by `IncomingCallHost`, which `AppOverlays` excludes from a call window,
       * so on the desktop the WORKSPACE follows the merge — by opening a window
       * for the survivor — and the call window on the ended call is told
       * nothing at all. It therefore never set `endedReason`, never armed the
       * ended auto-close, and could not even self-heal through the grant
       * (`staleTime: Infinity`, `retry: false`): a dead room with a working
       * Hang up button, in the taskbar, for the rest of the session, still
       * reporting live media and still claiming the media hotkeys and the tray.
       *
       * So it is recorded rather than swallowed. `merged` is not an
       * `endedReason` — there is no end screen for it, in a browser or
       * anywhere else, and the browser path is byte-for-byte what it was.
       */
      if (event.type === 'call.ended' && event.reason === 'merged') {
        setMerged(true);
        return;
      }
      /* MOVED TO A MEETING IS NOT AN ENDING WITH A SCREEN EITHER. Recorded
         rather than swallowed, for the desktop call window that has no other
         way to learn its call is over — see `escalated` above. The media is
         released here because the meeting opens its own session in another
         room and this microphone must not still be publishing into the call's. */
      if (event.type === 'call.escalated') {
        leaveCallLocally(callId);
        setEscalated(true);
        return;
      }
      const reason = END_REASON[event.type];
      if (reason !== undefined) {
        /*
         * ONE PERSON SAYING NO IS NOT THE CALL ENDING.
         *
         * `call.declined` is fanned to the WHOLE audience unconditionally
         * (apps/api routes/calls.ts), while the call itself ends only when the
         * decliner was the last live counterpart. On a group call, or on a 1:1
         * with a simultaneous-ring delegate leg, the call carries on — and
         * every other client used to render "Call declined" and stop its
         * microphone anyway. The caller was cut off mid-ring, her participant
         * row stayed `connected` so she was busy-locked and uncallable until
         * the sweeper reaped her, and the person still ringing could pick up
         * into a room with nobody in it.
         *
         * THE PAYLOAD ALREADY KNOWS. `CallStateEvent` carries the whole `Call`,
         * read AFTER the terminal update at every fan-out site that announces
         * an ending, so its `status` is the server's own answer to "did this
         * actually end". The announcement is still obeyed by default — an
         * absent or unknown status ends the call, because refusing to would be
         * the 68-minute bug again — and only an explicit live status stops it.
         * The asymmetry is the point; see lib/callState.
         */
        if (callIsLive(announcedCallStatus(event))) return;
        leaveCallLocally(callId);
        setEndedReason(reason);
      }
    });
  }, [callId]);

  /*
   * AND THE SAME ENDING, WITHOUT THE SOCKET.
   *
   * Everything above depends on one frame reaching one handler. On 21 Aug one
   * did not — the access token expired 16 minutes into a call, the gateway
   * closed the socket at `exp`, and the reconnect loop re-presented the dead
   * token until the owner gave up. `call.ended` was written, published in 12ms
   * and delivered to a topic with nobody on it.
   *
   * That hole is fixed at the socket (lib/api/token.ts), and this is here
   * because it must not matter whether it stays fixed. Reconciliation asks the
   * server; the server's answer is the only thing that ends a call here, so
   * this can only ever be late, never wrong. It stops asking the moment the
   * call is settled by either route.
   */
  useCallReconciliation({
    callId,
    enabled: endedReason === null && !merged && !escalated,
    onSettled: (outcome) => {
      /*
       * THE MICROPHONE GOES BACK WHATEVER THE OUTCOME WAS — unconditional, and
       * above the merge branch on purpose.
       *
       * A merge learned by EVENT is genuinely not an ending for this user:
       * `call.merged` arrives on the same socket, `useTransferredCall` moves
       * them into the surviving call, and the unmount that follows releases the
       * room. That exception was copied verbatim onto this path — where nothing
       * moves anybody, because getting here at all means the socket did not
       * deliver. `merged` then disarmed the poll (`enabled` below), `callMerge`
       * deleted no room so the SFU could not evict either, and in a browser
       * `merged` is wired to nothing, so `<CallRoom>` kept rendering: a live
       * capture track publishing into the dead call's room, with no ceiling and
       * no second chance. That is the 21-August failure reproduced inside the
       * backstop written to prevent it.
       *
       * Releasing is safe here precisely because it is KEYED. If this document
       * has already moved into the surviving call, the registry holds THAT
       * call's id and this is a no-op; it can only ever stop the media of the
       * call that ended.
       */
      leaveCallLocally(callId);
      if (outcome.kind === 'merged') {
        setMerged(true);
        return;
      }
      setEndedReason(outcome.reason);
    },
  });

  return { connected, endedReason, merged, escalated };
}
